import { AMEND, CANCEL } from '@/src/constants/itinerary/fareFamily'
import { FareFamilyFareRule, JourneyTab, PriceBreakUpItem, Solution } from '@/src/types/fareFamilyInfo'
import { BaggageMeasurement, Fare, FareBenefit, PaxWiseBaggage } from '@/src/types/itinerary'
import { TranslationFunction } from '@/src/types/translations'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'

export const createJourneyTabs = (solutions: Solution[], selectedFare: Fare): JourneyTab[] => {
    if (!solutions || !Array.isArray(solutions) || isEmpty(solutions)) {
        return []
    }

    return solutions.map((solution, index) => {
        const originDestinationInfo = solution?.originDestinationInfo || {}
        const { originCityName = '', destinationCityName = '' } = originDestinationInfo
        const mapKey = String(index + 1) as '1' | '2'
        const fareGroupName = selectedFare?.fareGroupInformationMap[mapKey]?.fareGroupDisplayName || ''
        const label = `${originCityName} - ${destinationCityName}${fareGroupName ? ` : ${fareGroupName}` : ''}`
        return {
            label,
            value: index
        }
    })
}

const formatTimestamp = (timestamp: string, formatDate: (date: string, format: string) => string): string => {
    if (!timestamp) return ''

    const timestampNumber = Number(timestamp)
    if (!timestampNumber) return ''

    const dateString = new Date(timestampNumber).toISOString()
    return formatDate(dateString, 'DD MMM, HH:mm')
}

export const processFareRuleCharges = (
    fareRule: FareFamilyFareRule,
    formatDate: (date: string, format: string) => string,
    formatCurrency: (amount: number) => string,
    t: TranslationFunction<'common'>
) => {
    const { type, charges = [] } = fareRule
    const timeSegments: string[] = []
    const priceSegments: { priceValue: string; priceBreakUp: PriceBreakUpItem[] }[] = []

    charges.forEach(rule => {
        const { timeline: { endTime = '' } = {}, amount, permitted, priceBreakUp = [] } = rule || {}
        const formattedTime = formatTimestamp(endTime, formatDate)
        timeSegments.push(formattedTime)

        if (type === CANCEL) {
            const { amountRefundable } = amount || {}
            const priceValue = !permitted ? t('nonRefundable') : formatCurrency(amountRefundable)

            priceSegments.push({ priceValue, priceBreakUp: permitted ? priceBreakUp : [] })
        } else if (type === AMEND) {
            const { totalCharge } = amount || {}
            const priceValue = !permitted ? t('nonChangeable') : formatCurrency(totalCharge || 0)
            priceSegments.push({ priceValue, priceBreakUp: permitted ? priceBreakUp : [] })
        }
    })

    return { timeSegments, priceSegments }
}

export const getBenefitKeys = (fareBenefits: FareBenefit[]): string[] => {
    if (Array.isArray(fareBenefits)) {
        return fareBenefits.map(benefitObj => benefitObj.benefitKey)
    }
    return []
}

export const getFareBrandDetails = (fare: Fare, tabValue : 0 | 1) => {
    const { fareGroupInformation, displayTags, fareGroupInformationMap } = fare || {}
    const { fareTag } = displayTags || {}
    const { key: fareTagType = '', value: fareTagDisplayValue = '' } = fareTag || {}
    const mapKey = String(tabValue + 1) as '1' | '2'
    const { fareGroupName = '', fareGroupDisplayName: fareTitle = '', ctBrandName = '' } = fareGroupInformationMap?.[mapKey] || {}
    return { fareGroupName, fareTitle, ctBrandName, fareTagType, fareTagDisplayValue }
}

export const getWeight = (measurements: BaggageMeasurement[]) => {
    let weightObj: { value: string; unit: string } = { value: '', unit: '' }
    if (isEmpty(measurements) || !Array.isArray(measurements)) {
        return weightObj
    }
    weightObj = measurements.find(measurement => {
        return measurement?.type === 'Weight'
    }) as BaggageMeasurement
    return weightObj
}

export const processBaggageData = (baggage: PaxWiseBaggage) => {
    const { value, unit } = getWeight(baggage?.measurements) || {}
    const { baggageInfoText: subtext = '' } = baggage || {}
    const displayValue = unit && value ? `${value} ${unit}` : ''
    const isNotAllowed = !displayValue
    const isWarningInfo = isNotAllowed && subtext?.toUpperCase() === 'NOT_ALLOWED'

    return {
        displayValue,
        subtext,
        isWarningInfo
    }
}

export const getFareFamilyPrice = (strikeOff: { strikedOffAmount: number; displayAmount: number }, price: number) => {
    const { strikedOffAmount, displayAmount } = strikeOff || {}
    let displayPrice = 0
    let strikeoffPrice = 0
    let showStrikeoffPrice = false
    if (!isEmpty(strikedOffAmount)) {
        strikeoffPrice = strikedOffAmount
        displayPrice = displayAmount
        showStrikeoffPrice = Math.floor(displayPrice) < Math.floor(strikeoffPrice)
    } else {
        displayPrice = price
    }
    return {
        displayPrice,
        strikeoffPrice,
        showStrikeoffPrice
    }
}
